/**
 * Overlay theme system.
 *
 * Provides color palettes for overlay elements (measurement labels, rulers)
 * that adapt to the current drawing background color (light or dark).
 */

export interface OverlayColors {
  // Measurement labels
  labelBackground: string;
  labelText: string;
  labelBorder: string;
  guideLineColor: string;

  // Ruler
  rulerBackground: string;
  rulerLineColor: string;
  rulerTextColor: string;
  rulerOriginColor: string;
  rulerHandleBorder: string;
}

const LIGHT_THEME: OverlayColors = {
  labelBackground: "rgba(255, 255, 255, 0.85)",
  labelText: "#333333",
  labelBorder: "#999999",
  guideLineColor: "rgba(100, 100, 100, 0.5)",

  rulerBackground: "rgba(245, 245, 245, 0.5)",
  rulerLineColor: "rgba(125, 125, 125, 0.8)",
  rulerTextColor: "rgba(51, 51, 51, 0.8)",
  rulerOriginColor: "#ff4444",
  rulerHandleBorder: "#ffffff",
};

const DARK_THEME: OverlayColors = {
  labelBackground: "rgba(40, 40, 40, 0.9)",
  labelText: "#eeeeee",
  labelBorder: "#666666",
  guideLineColor: "rgba(255, 255, 255, 0.8)",

  rulerBackground: "rgba(30, 30, 30, 0.5)",
  rulerLineColor: "rgba(160, 160, 160, 0.8)",
  rulerTextColor: "rgba(220, 220, 220, 0.8)",
  rulerOriginColor: "#ff6666",
  rulerHandleBorder: "#333333",
};

/**
 * Determine if a background color (as 0xRRGGBB integer) is dark.
 * Uses relative luminance with a threshold of 0.5.
 */
export function isDarkBackground(backgroundColor: number): boolean {
  const r = (backgroundColor >> 16) & 0xff;
  const g = (backgroundColor >> 8) & 0xff;
  const b = backgroundColor & 0xff;
  // Relative luminance (ITU-R BT.709)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

/**
 * Get the overlay color palette for the given background color.
 */
export function getOverlayTheme(backgroundColor: number): OverlayColors {
  return isDarkBackground(backgroundColor) ? DARK_THEME : LIGHT_THEME;
}
